import { useState, useCallback } from "react";

import { format, parseISO } from "date-fns";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const presetLabels = {
  "7days": "Last 7 Days",
  "30days": "Last 30 Days",
  "90days": "Last 90 Days",
  "12months": "Last 12 Months",
  ytd: "Year to Date",
};

/**
 * Format a number as full-precision US currency, e.g. "$124,592".
 */
const fmtCurrency = (v) => {
  if (v == null || isNaN(v)) return "$0";

  return `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};

/**
 * Build a human-readable label for the selected date range.
 */
const getDateRangeLabel = (dateRange) => {
  if (!dateRange) return "All Time";

  if (dateRange.type === "custom" && dateRange.start && dateRange.end) {
    const s = format(parseISO(dateRange.start), "MMM d, yyyy");
    const e = format(parseISO(dateRange.end), "MMM d, yyyy");

    return `${s} - ${e}`;
  }

  return presetLabels[dateRange.preset] || "Last 30 Days";
};

/**
 * Trigger a file download in the browser.
 */
const downloadBlob = (blob, filename) => {
  const link = document.createElement("a");

  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

/**
 * Return today's date as YYYY-MM-DD for filenames.
 */
const todayISO = () => new Date().toISOString().split("T")[0];

// ---------------------------------------------------------------------------
// CSV builders (one per tab)
// ---------------------------------------------------------------------------

const buildOverviewCSV = (data) => {
  const rows = [
    ["Metric", "Value"],
    ["Total Revenue", fmtCurrency(data.revenue?.lifetime)],
    ["Monthly Revenue", fmtCurrency(data.revenue?.monthly)],
    ["Active Customers", data.customers?.total ?? 0],
    ["Open Tickets", data.tickets?.open ?? 0],
    ["Collection Rate", `${data.invoices?.collectionRate ?? 0}%`],
  ];

  return rows.map((r) => r.join(",")).join("\n");
};

const buildRevenueCSV = (data) => {
  const rev = data.revenue || {};
  const categories = rev.categories || [];
  const chartData = rev.chartData || [];

  const sections = [];

  // Monthly revenue table
  sections.push("Month,Revenue");
  categories.forEach((month, i) => {
    sections.push(`${month},${fmtCurrency(chartData[i] ?? 0)}`);
  });

  // Blank separator
  sections.push("");

  // Top products
  sections.push("Product,Revenue,Percentage");
  (rev.topProducts || []).forEach((p) => {
    sections.push(`${p.name},${fmtCurrency(p.revenue)},${p.percentage}%`);
  });

  return sections.join("\n");
};

const buildCustomersCSV = (data) => {
  const c = data.customers || {};

  const sections = [];

  sections.push("Metric,Value");
  sections.push(`Total Customers,${c.total ?? 0}`);
  sections.push(`Retention Rate,${c.retentionRate ?? 0}%`);
  sections.push(`Churn Rate,${c.churnRate ?? 0}%`);

  sections.push("");

  sections.push("Plan Type,Customers");
  (c.byPlan || []).forEach((p) => {
    sections.push(`${p.label},${p.value}`);
  });

  return sections.join("\n");
};

const buildSupportCSV = (data) => {
  const t = data.tickets || {};

  const sections = [];

  sections.push("Metric,Value");
  sections.push(`Open Tickets,${t.open ?? 0}`);
  sections.push(`Total Tickets,${t.total ?? 0}`);
  sections.push(`Avg Resolution,${t.avgResolutionTime ?? "N/A"}`);
  sections.push(`Satisfaction,${t.satisfaction ?? "N/A"}`);

  sections.push("");

  sections.push("Agent,Tickets Resolved,Satisfaction");
  (t.agents || []).forEach((a) => {
    sections.push(`${a.name},${a.tickets},${a.satisfaction}`);
  });

  return sections.join("\n");
};

const buildInvoicesCSV = (data) => {
  const inv = data.invoices || {};

  const sections = [];

  sections.push("Metric,Value");
  sections.push(`Total Invoiced,${fmtCurrency(inv.totalAmount)}`);
  sections.push(`Pending,${fmtCurrency(inv.pendingAmount)}`);
  sections.push(`Overdue,${fmtCurrency(inv.overdueAmount)}`);
  sections.push(`Collection Rate,${inv.collectionRate ?? 0}%`);

  sections.push("");

  sections.push("Age Range,Count,Amount,Percentage");
  (inv.aging || []).forEach((row) => {
    sections.push(
      `${row.range},${row.count},${fmtCurrency(row.amount)},${row.percentage}%`
    );
  });

  return sections.join("\n");
};

const csvBuilders = {
  overview: buildOverviewCSV,
  revenue: buildRevenueCSV,
  customers: buildCustomersCSV,
  support: buildSupportCSV,
  invoices: buildInvoicesCSV,
};

// ---------------------------------------------------------------------------
// PDF helpers
// ---------------------------------------------------------------------------

/**
 * Shared constants for PDF layout.
 */
const PDF = {
  marginLeft: 14,
  marginRight: 14,
  headerY: 15,
  titleSize: 20,
  subtitleSize: 10,
  bodySize: 10,
  smallSize: 9,
  lineColor: [99, 102, 241], // indigo-500
  tableHeadBg: [99, 102, 241],
  tableHeadText: [255, 255, 255],
  tableAltRow: [248, 250, 252],
};

/**
 * Draw the standard page header on the current page.
 * Returns the Y position after the header.
 */
const drawPageHeader = (doc, dateRangeLabel, sectionTitle) => {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Title
  doc.setFontSize(PDF.titleSize);
  doc.setFont("helvetica", "bold");
  doc.text("Lunova - Reports", PDF.marginLeft, PDF.headerY);

  // Section subtitle (if provided)
  let y = PDF.headerY + 7;

  if (sectionTitle) {
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(99, 102, 241);
    doc.text(sectionTitle, PDF.marginLeft, y);
    doc.setTextColor(0, 0, 0);
    y += 5;
  }

  // Date range
  doc.setFontSize(PDF.subtitleSize);
  doc.setFont("helvetica", "normal");
  doc.text(`Report period: ${dateRangeLabel}`, PDF.marginLeft, y);
  y += 3;

  // Horizontal rule
  doc.setDrawColor(...PDF.lineColor);
  doc.setLineWidth(0.5);
  doc.line(PDF.marginLeft, y, pageWidth - PDF.marginRight, y);

  return y + 8;
};

/**
 * Draw the standard page footer.
 */
const drawPageFooter = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const y = pageHeight - 10;

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(156, 163, 175); // gray-400

  const generated = `Generated on ${format(new Date(), "MMM d, yyyy 'at' h:mm a")}`;

  doc.text(generated, PDF.marginLeft, y);

  const brand = "Lunova Dashboard";
  const brandWidth = doc.getTextWidth(brand);

  doc.text(brand, pageWidth - PDF.marginRight - brandWidth, y);

  doc.setTextColor(0, 0, 0);
};

/**
 * Draw the top-level KPI summary row.
 * Returns the Y position below the KPIs.
 */
const drawKPIRow = (doc, data, startY) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const usable = pageWidth - PDF.marginLeft - PDF.marginRight;
  const colWidth = usable / 4;

  const kpis = [
    { label: "Total Revenue", value: fmtCurrency(data.revenue?.lifetime) },
    { label: "Customers", value: String(data.customers?.total ?? 0) },
    { label: "Open Tickets", value: String(data.tickets?.open ?? 0) },
    {
      label: "Collection Rate",
      value: `${data.invoices?.collectionRate ?? 0}%`,
    },
  ];

  doc.setFontSize(PDF.smallSize);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(107, 114, 128); // gray-500

  kpis.forEach((kpi, i) => {
    const x = PDF.marginLeft + i * colWidth;

    doc.text(kpi.label, x, startY);
  });

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);

  kpis.forEach((kpi, i) => {
    const x = PDF.marginLeft + i * colWidth;

    doc.text(kpi.value, x, startY + 7);
  });

  return startY + 16;
};

// ---------------------------------------------------------------------------
// PDF section builders
// ---------------------------------------------------------------------------

const addRevenueSection = (doc, data, startY) => {
  const rev = data.revenue || {};
  const categories = rev.categories || [];
  const chartData = rev.chartData || [];

  const tableBody = categories.map((month, i) => [
    month,
    fmtCurrency(chartData[i] ?? 0),
  ]);

  doc.autoTable({
    startY,
    head: [["Month", "Revenue"]],
    body: tableBody,
    theme: "grid",
    headStyles: {
      fillColor: PDF.tableHeadBg,
      textColor: PDF.tableHeadText,
      fontStyle: "bold",
      fontSize: PDF.smallSize,
    },
    bodyStyles: { fontSize: PDF.smallSize },
    alternateRowStyles: { fillColor: PDF.tableAltRow },
    margin: { left: PDF.marginLeft, right: PDF.marginRight },
  });

  let y = doc.lastAutoTable.finalY + 8;

  // Top products sub-table
  if (rev.topProducts && rev.topProducts.length > 0) {
    doc.setFontSize(PDF.bodySize);
    doc.setFont("helvetica", "bold");
    doc.text("Top Products", PDF.marginLeft, y);
    y += 4;

    doc.autoTable({
      startY: y,
      head: [["Product", "Revenue", "Percentage"]],
      body: rev.topProducts.map((p) => [
        p.name,
        fmtCurrency(p.revenue),
        `${p.percentage}%`,
      ]),
      theme: "grid",
      headStyles: {
        fillColor: PDF.tableHeadBg,
        textColor: PDF.tableHeadText,
        fontStyle: "bold",
        fontSize: PDF.smallSize,
      },
      bodyStyles: { fontSize: PDF.smallSize },
      alternateRowStyles: { fillColor: PDF.tableAltRow },
      margin: { left: PDF.marginLeft, right: PDF.marginRight },
    });
  }

  return doc.lastAutoTable.finalY;
};

const addCustomersSection = (doc, data, startY) => {
  const c = data.customers || {};

  // Summary metrics
  const summaryBody = [
    ["Total Customers", String(c.total ?? 0)],
    ["Retention Rate", `${c.retentionRate ?? 0}%`],
    ["Churn Rate", `${c.churnRate ?? 0}%`],
    ["New This Month", String(c.newThisMonth ?? 0)],
  ];

  doc.autoTable({
    startY,
    head: [["Metric", "Value"]],
    body: summaryBody,
    theme: "grid",
    headStyles: {
      fillColor: PDF.tableHeadBg,
      textColor: PDF.tableHeadText,
      fontStyle: "bold",
      fontSize: PDF.smallSize,
    },
    bodyStyles: { fontSize: PDF.smallSize },
    alternateRowStyles: { fillColor: PDF.tableAltRow },
    margin: { left: PDF.marginLeft, right: PDF.marginRight },
  });

  let y = doc.lastAutoTable.finalY + 8;

  // Plan breakdown
  if (c.byPlan && c.byPlan.length > 0) {
    doc.setFontSize(PDF.bodySize);
    doc.setFont("helvetica", "bold");
    doc.text("Customer Segments by Plan", PDF.marginLeft, y);
    y += 4;

    doc.autoTable({
      startY: y,
      head: [["Plan Type", "Customers"]],
      body: c.byPlan.map((p) => [p.label, String(p.value)]),
      theme: "grid",
      headStyles: {
        fillColor: PDF.tableHeadBg,
        textColor: PDF.tableHeadText,
        fontStyle: "bold",
        fontSize: PDF.smallSize,
      },
      bodyStyles: { fontSize: PDF.smallSize },
      alternateRowStyles: { fillColor: PDF.tableAltRow },
      margin: { left: PDF.marginLeft, right: PDF.marginRight },
    });
  }

  return doc.lastAutoTable.finalY;
};

const addSupportSection = (doc, data, startY) => {
  const t = data.tickets || {};

  const summaryBody = [
    ["Open Tickets", String(t.open ?? 0)],
    ["Total Tickets", String(t.total ?? 0)],
    ["Avg Resolution", t.avgResolutionTime ?? "N/A"],
    ["Satisfaction", t.satisfaction ?? "N/A"],
    ["First Response", t.firstResponseTime ?? "N/A"],
  ];

  doc.autoTable({
    startY,
    head: [["Metric", "Value"]],
    body: summaryBody,
    theme: "grid",
    headStyles: {
      fillColor: PDF.tableHeadBg,
      textColor: PDF.tableHeadText,
      fontStyle: "bold",
      fontSize: PDF.smallSize,
    },
    bodyStyles: { fontSize: PDF.smallSize },
    alternateRowStyles: { fillColor: PDF.tableAltRow },
    margin: { left: PDF.marginLeft, right: PDF.marginRight },
  });

  let y = doc.lastAutoTable.finalY + 8;

  // Agent performance
  if (t.agents && t.agents.length > 0) {
    doc.setFontSize(PDF.bodySize);
    doc.setFont("helvetica", "bold");
    doc.text("Agent Performance", PDF.marginLeft, y);
    y += 4;

    doc.autoTable({
      startY: y,
      head: [["Agent", "Tickets Resolved", "Satisfaction"]],
      body: t.agents.map((a) => [a.name, String(a.tickets), a.satisfaction]),
      theme: "grid",
      headStyles: {
        fillColor: PDF.tableHeadBg,
        textColor: PDF.tableHeadText,
        fontStyle: "bold",
        fontSize: PDF.smallSize,
      },
      bodyStyles: { fontSize: PDF.smallSize },
      alternateRowStyles: { fillColor: PDF.tableAltRow },
      margin: { left: PDF.marginLeft, right: PDF.marginRight },
    });
  }

  return doc.lastAutoTable.finalY;
};

const addInvoicesSection = (doc, data, startY) => {
  const inv = data.invoices || {};

  const summaryBody = [
    ["Total Invoiced", fmtCurrency(inv.totalAmount)],
    ["Pending", fmtCurrency(inv.pendingAmount)],
    ["Overdue", fmtCurrency(inv.overdueAmount)],
    ["Collection Rate", `${inv.collectionRate ?? 0}%`],
  ];

  doc.autoTable({
    startY,
    head: [["Metric", "Value"]],
    body: summaryBody,
    theme: "grid",
    headStyles: {
      fillColor: PDF.tableHeadBg,
      textColor: PDF.tableHeadText,
      fontStyle: "bold",
      fontSize: PDF.smallSize,
    },
    bodyStyles: { fontSize: PDF.smallSize },
    alternateRowStyles: { fillColor: PDF.tableAltRow },
    margin: { left: PDF.marginLeft, right: PDF.marginRight },
  });

  let y = doc.lastAutoTable.finalY + 8;

  // Aging report
  if (inv.aging && inv.aging.length > 0) {
    doc.setFontSize(PDF.bodySize);
    doc.setFont("helvetica", "bold");
    doc.text("Aging Report", PDF.marginLeft, y);
    y += 4;

    doc.autoTable({
      startY: y,
      head: [["Age Range", "Count", "Amount", "Percentage"]],
      body: inv.aging.map((row) => [
        row.range,
        String(row.count),
        fmtCurrency(row.amount),
        `${row.percentage}%`,
      ]),
      theme: "grid",
      headStyles: {
        fillColor: PDF.tableHeadBg,
        textColor: PDF.tableHeadText,
        fontStyle: "bold",
        fontSize: PDF.smallSize,
      },
      bodyStyles: { fontSize: PDF.smallSize },
      alternateRowStyles: { fillColor: PDF.tableAltRow },
      margin: { left: PDF.marginLeft, right: PDF.marginRight },
    });
  }

  return doc.lastAutoTable.finalY;
};

const pdfSectionBuilders = {
  overview: null, // overview uses KPI row only, no extra table
  revenue: addRevenueSection,
  customers: addCustomersSection,
  support: addSupportSection,
  invoices: addInvoicesSection,
};

const tabDisplayNames = {
  overview: "Overview",
  revenue: "Revenue",
  customers: "Customers",
  support: "Support",
  invoices: "Invoices",
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Provides CSV and PDF export actions for the Reports page.
 *
 * @param {object} data       - Full return from useReportsData (spread)
 * @param {object} dateRange  - { type, preset, start, end }
 * @param {string} activeTab  - 'overview' | 'revenue' | 'customers' | 'support' | 'invoices'
 *
 * @returns {{ exportCSV, exportPDF, exportFullPDF, exporting }}
 */
export const useExport = (data, dateRange, activeTab) => {
  const [exporting, setExporting] = useState(false);

  // ----- CSV ---------------------------------------------------------------

  const exportCSV = useCallback(() => {
    if (!data) return;

    setExporting(true);

    try {
      const builder = csvBuilders[activeTab] || csvBuilders.overview;
      const csvContent = builder(data);

      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      downloadBlob(blob, `lunova-reports-${activeTab}-${todayISO()}.csv`);
    } catch (err) {
      console.error("CSV export failed:", err);
    } finally {
      setExporting(false);
    }
  }, [data, activeTab]);

  // ----- PDF (single tab) --------------------------------------------------

  const exportPDF = useCallback(async () => {
    if (!data) return;

    setExporting(true);

    try {
      const { default: jsPDF } = await import("jspdf");

      await import("jspdf-autotable");

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const dateLabel = getDateRangeLabel(dateRange);

      // Header
      let y = drawPageHeader(
        doc,
        dateLabel,
        tabDisplayNames[activeTab] || "Overview"
      );

      // KPI row
      y = drawKPIRow(doc, data, y);

      // Tab-specific table section
      const sectionBuilder = pdfSectionBuilders[activeTab];

      if (sectionBuilder) {
        sectionBuilder(doc, data, y);
      }

      // Footer
      drawPageFooter(doc);

      const filename = `lunova-reports-${activeTab}-${todayISO()}.pdf`;

      doc.save(filename);
    } catch (err) {
      console.error("PDF export failed:", err);
    } finally {
      setExporting(false);
    }
  }, [data, dateRange, activeTab]);

  // ----- Full report PDF (all tabs) ----------------------------------------

  const exportFullPDF = useCallback(async () => {
    if (!data) return;

    setExporting(true);

    try {
      const { default: jsPDF } = await import("jspdf");

      await import("jspdf-autotable");

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const dateLabel = getDateRangeLabel(dateRange);
      const tabs = ["overview", "revenue", "customers", "support", "invoices"];

      tabs.forEach((tab, index) => {
        if (index > 0) {
          doc.addPage();
        }

        // Header with section title
        let y = drawPageHeader(doc, dateLabel, tabDisplayNames[tab]);

        // KPI row on every page
        y = drawKPIRow(doc, data, y);

        // Tab-specific content
        const sectionBuilder = pdfSectionBuilders[tab];

        if (sectionBuilder) {
          sectionBuilder(doc, data, y);
        }

        // Footer on every page
        drawPageFooter(doc);
      });

      const filename = `lunova-reports-full-${todayISO()}.pdf`;

      doc.save(filename);
    } catch (err) {
      console.error("Full PDF export failed:", err);
    } finally {
      setExporting(false);
    }
  }, [data, dateRange]);

  return { exportCSV, exportPDF, exportFullPDF, exporting };
};
